import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { formatCredits } from '@casino/core';

interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'warn' | 'bad';
}
const ToastCtx = createContext<(text: string, kind?: Toast['kind']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-3), { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-host" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

export function credits(units: string | bigint | null | undefined): string {
  if (units === null || units === undefined) return '—';
  return formatCredits(typeof units === 'string' ? BigInt(units) : units);
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="modal-back" role="dialog" aria-modal="true" aria-label={title} onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="modal" style={wide ? { width: 'min(1100px, 100%)' } : undefined} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{title}</h2>
          <button className="btn small" onClick={onClose} autoFocus>
            Close
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function errMsg(e: unknown): string {
  const anyE = e as { code?: string; message?: string };
  return anyE?.code ? `${anyE.code}: ${anyE.message}` : String(anyE?.message ?? e);
}
